import '../bootstrap';
import '../styles/index.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/700.css';
import '@fontsource/dm-mono/400.css';
import '@fontsource/dm-mono/500.css';
import posthog from 'posthog-js';
import { Fragment, ReactNode, useEffect } from 'react';
import { NextComponentType, NextPageContext } from 'next';
import type { AppProps } from 'next/app';
import DefaultLayout from '../layouts/Default';
import Head from 'next/head';
import { APP_NAME_URL, APP_SHORT_BLURB, DEPLOYED_URL } from '../constants';
import { useRouter } from 'next/router';
import { RecoilRoot } from 'recoil';

if (
  typeof window !== 'undefined' &&
  !window.location.host.includes('127.0.0.1') &&
  !window.location.host.includes('localhost')
  // && !window.location.host.includes('teoiudfsjlj90j309dfjlsd309jf')
) {
  posthog.init('phc_PwEAHs9aqjON8Jhp7HdcwNjKaRStD4jWPdu5iEOl19e', { api_host: 'https://e.silo.finance' });
}

if (typeof window !== 'undefined' && !!window.ethereum) {
  window.ethereum.autoRefreshOnNetworkChange = false;
}

function MyApp({
  Component,
  pageProps,
}: AppProps & {
  Component: NextComponentType<NextPageContext> & {
    Guard: ({}: { children?: ReactNode }) => JSX.Element;
    Layout: ({}: { children?: ReactNode }) => JSX.Element;
    Provider: ({}: { children?: ReactNode }) => JSX.Element;
  };
}) {
  const router = useRouter();

  //pageview tracking in SPA
  useEffect(() => {
    posthog.capture('pageview');
  }, [router.pathname]);

  // Allows for conditionally setting a provider, layout or guard to be hoisted per page
  const Provider = Component.Provider || Fragment;
  const Layout = Component.Layout || DefaultLayout;
  const Guard = Component.Guard || Fragment;

  return (
    <Fragment>
      <Head>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no"
        />
        <title key="title">{APP_NAME_URL}</title>

        <meta key="description" name="description" content={APP_SHORT_BLURB} />

        <meta name="application-name" content="Silo Finance App" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="Silo Finance" />

        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content="#11161f" />

        <meta key="twitter:card" name="twitter:card" content="app" />
        <meta key="twitter:title" name="twitter:title" content="Silo Finance App" />
        <meta key="twitter:url" name="twitter:url" content={DEPLOYED_URL} />
        <meta key="twitter:description" name="twitter:description" content={APP_SHORT_BLURB} />
        <meta key="twitter:image" name="twitter:image" content={DEPLOYED_URL + '/social-share.png'} />
        <meta key="twitter:creator" name="twitter:creator" content="@SiloFinance" />
        <meta key="og:title" property="og:title" content="Silo Finance App" />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content="Silo Finance App" />
        <meta key="og:url" property="og:url" content={DEPLOYED_URL} />
        <meta key="og:image" property="og:image" content={DEPLOYED_URL + '/social-share.png'} />
        <meta key="og:description" property="og:description" content={APP_SHORT_BLURB} />
      </Head>
      <RecoilRoot>
        <Provider {...pageProps}>
          <Layout>
            <Guard>
              <Component {...pageProps} />
            </Guard>
          </Layout>
        </Provider>
      </RecoilRoot>
    </Fragment>
  );
}

export default MyApp;
